// @flow
import type { Country } from 'types/country';
import type { StoreState } from 'reducers';
import type { RequestStates } from 'types/redux';
import React, { Component } from 'react';
import _ from 'lodash';
import ConfirmationModal from 'components/modals/confirmationModal';
import i18n from 'i18n-js';
import { Form } from 'semantic-grab-ui/src';
import { connect } from 'react-redux';
import { getCountries } from 'actions/countries';
import { isRequestsPending } from 'utils/request-state-utils';

type Props = {
  value: number,
  countries: Array<Country>,
  getCountries: () => void,
  getCountriesRequest: RequestStates,
  disableConfirm: boolean,
  countryCode: boolean,
  onChange: (_: any, _: { country: Country }) => void,
  disabled: boolean,
};

const CUSTOM_PROPS = ['countries', 'getCountries', 'getCountriesRequest', 'disableConfirm', 'countryCode'];

export class CountrySelect extends Component {
  props: Props;

  constructor(props: Props) {
    super(props);

    this.state = { openConfirmation: false, selectedData: null };
  }

  static defaultProps = {
    countries: [],
    disableConfirm: false,
  };

  componentDidMount() {
    if (_.isEmpty(this.props.countries) && !isRequestsPending(this.props.getCountriesRequest)) {
      this.props.getCountries();
    } else {
      this.forceUpdateCountry(this.props);
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    // Force invoke onChange callback so if a countryID is given, consumer of
    // this component still can get current data of that countryID
    if (_.isEmpty(this.props.countries) && _.some(nextProps.countries)) {
      this.forceUpdateCountry(nextProps);
    }
  }

  forceUpdateCountry(props: Props) {
    let check = 'countryID';
    if (props.countryCode) {
      check = 'code';
    }
    const country = _.find(props.countries, [check, props.value]);
    if (country) {
      props.onChange({}, { country });
    }
  }

  onSelectConfirmation = (data: any) => {
    const selectedData = data || this.state.selectedData;
    if (selectedData && this.props.onChange) {
      let check = 'countryID';
      if (this.props.countryCode) {
        check = 'code';
      }
      const country = _.find(this.props.countries, [check, selectedData.value]);
      this.props.onChange(event, { ...selectedData, country });
      this.setState({ openConfirmation: false });
    }
  }

  onSelectChange = (event: any, data: Object) => {
    if (this.props.value === 0 || this.props.value === '') {
      this.setState({ selectedData: data });
      this.onSelectConfirmation(data);
    } else if (this.props.value !== data.value) {
      if (this.props.disableConfirm) {
        this.setState({ selectedData: data });
        this.onSelectConfirmation(data);
      } else {
        // Show confirm dialog
        this.setState({ openConfirmation: true, selectedData: data });
      }
    }
  };

  onClose = () => {
    this.setState({ openConfirmation: false });
  }

  render() {
    const { countries, getCountriesRequest, countryCode, disabled } = this.props;
    let countriesList;
    if (countryCode) {
      countriesList = countries.map(country => ({
        key: country.code,
        text: country.name,
        value: country.code,
      }));
    } else {
      countriesList = countries.map(country => ({
        key: country.countryID,
        text: country.name,
        value: country.countryID,
      }));
    }
    const controlProps = _.omit(this.props, CUSTOM_PROPS);
    const { openConfirmation } = this.state;
    return (
      <span className="field">
        <Form.Select
          {...controlProps}
          options={countriesList}
          loading={isRequestsPending(getCountriesRequest)}
          onChange={this.onSelectChange}
          disabled={disabled}
        />
        <ConfirmationModal
          header={i18n.t('confirmation_modal.header_change_country')}
          content={i18n.t('confirmation_modal.content_change_country')}
          onConfirmation={() => { this.onSelectConfirmation(); }}
          onClose={this.onClose}
          open={openConfirmation}
          yesText={i18n.t('confirmation_modal.yes_change_country')}
          noText={i18n.t('confirmation_modal.no_keep_country')}
          disabled={disabled}
        />
      </span>
    );
  }
}

export function mapStateToProps(state: StoreState) {
  return {
    countries: state.countries.entities,
    getCountriesRequest: state.requests.getCountriesRequest || {},
  };
}

export default connect(mapStateToProps, {
  getCountries,
})(CountrySelect);

CountrySelect.propTypes = {
  ...Form.Select.propTypes,
};
